import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Timer, CalendarCheck, MapPin, Leaf, Sparkles, ShieldCheck, Truck, Package, MessageSquareText, ChevronDown } from "lucide-react";
import Layout from "../common/Layout";
import { usePageMeta } from "../common/usePageMeta";
import EhoBadge from "../common/EhoBadge";

const fade    = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.25 } } };
const stagger = { show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } };

// TODO: swap for a real local-delivery / farm shot once available in /public/images.
const benefits = [
  {
    img: "/images/products/seedlings/IMG_4846.JPEG",
    alt: "Rows of microgreen trays growing under lights in the controlled-climate grow room",
    title: "Predictable, restaurant-grade quality",
    body: "Same climate, same lighting, every day of the year. What you order in January tastes and looks like what you order in July.",
    icon: Sparkles,
  },
  {
    img: "/images/products/genoBasil/IMG_4479.JPEG",
    alt: "Close-up of clean, pesticide-free basil leaves",
    title: "Zero pesticides, genuinely",
    body: "Grown under biological pest control, not chemical spray. Worth knowing if your menu, or your customers, care where their food comes from.",
    icon: ShieldCheck,
  },
  {
    img: null,
    alt: "TODO: local delivery or farm exterior shot",
    title: "Hyper-local and genuinely fresh",
    body: "Cut to order, a few miles from Hockley. It's on your delivery within hours, not days out of cold storage.",
    icon: Truck,
  },
];

const kpis = [
  { num: "1–3 hrs", label: "Harvest to delivery", icon: Timer },
  { num: "52 wks",  label: "Grown year-round, same conditions", icon: CalendarCheck },
  { num: "~3 mi",   label: "From farm to your kitchen", icon: MapPin },
  { num: "0",       label: "Chemical pesticides", icon: Leaf },
];

const specs = [
  { title: "Lead time",  body: "Standing orders weekly. New items: 1–3 months depending on crop.", icon: Timer },
  { title: "MOQ",        body: "Trials from a few packs/bundles, scaling to standing orders of ~1–5 kg mixed per week.", icon: Package },
  { title: "Delivery",   body: "Short routes with pre-defined delivery windows. Invoicing bi-weekly.", icon: Truck },
];

const faqs = [
  { q: "How do you keep quality consistent all year?", a: "Everything is grown indoors under the same controlled climate and lighting, whatever the season outside. Nothing depends on weather." },
  { q: "Which products are available year-round?",  a: "Any crop can be made available year-round given sufficient lead time." },
  { q: "What are the typical order sizes?",         a: "(Crop dependent) Weekly standing orders from ~1/2–5 kg mixed. Small minimum orders for trials." },
  { q: "Delivery days & areas?",                   a: "Local drops 2–3 times per week around Hockley/Rochford/Southend area. If you're nearby, I'll fit you in." },
];

export default function PartnerPage() {
  usePageMeta({ title: 'Wholesale for Chefs in Essex | TearDrop Hydro', description: 'Direct supply of hydroponically grown herbs, leaves and edible stems for restaurants around Hockley, Rochford and Southend.', path: '/partner' });

  const [status, setStatus] = useState(""); // "success" | "error" | ""
  const [loading, setLoading] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [apiErr, setApiErr] = useState("");
  const formRef = useRef(null);

  async function handleEnquirySubmit(e) {
    e.preventDefault();
    setStatus("");
    setApiErr("");

    const form = formRef.current;
    if (!form) return;

    const hp = form.querySelector('input[name="b_username_verification"]')?.value?.trim();
    if (hp) {
      setStatus("success");
      form.reset();
      setMsgCount(0);
      return;
    }

    const fd = new FormData(form);
    const businessName = fd.get("businessName")?.toString().trim();
    const email = fd.get("email")?.toString().trim();
    const message = fd.get("message")?.toString().trim();

    if (!businessName || !email || !message || message.length < 10) {
      setStatus("error");
      setApiErr("Please complete all fields (tell us what you need in at least 10 characters).");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("https://teardrophydro.co.uk/garageFarm/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: businessName, email, message, role: "Partner enquiry", preferred: "Email" })
      });

      if (res.ok) {
        setStatus("success");
        form.reset();
        setMsgCount(0);
      } else {
        const errorData = await res.text();
        console.error("Server Error:", errorData);
        throw new Error("Failed");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setStatus("error");
      setApiErr("Couldn’t send via the form. You can email me directly at contact@teardrophydro.co.uk.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      {/* HERO */}
      <section style={{ position: "relative", overflow: "hidden", minHeight: "clamp(380px, 55svh, 620px)", display: "flex", alignItems: "center", marginBottom: "var(--s-6)" }}>
        <img
          src="/images/products/seedlings/IMG_4469.JPEG"
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,28,20,.82) 0%, rgba(10,28,20,.52) 60%, transparent 100%)" }} />
        <div className="container" style={{ position: "relative", zIndex: 1, padding: "var(--s-10) var(--s-5)" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="eyebrow" style={{ color: "var(--accent)", marginBottom: "var(--s-3)" }}>Partner with TearDrop</p>
            <h1 className="h1" style={{ color: "#fff", maxWidth: 580, marginTop: 0, marginBottom: "var(--s-4)", fontSize: "clamp(28px,5vw,44px)", lineHeight: 1.1 }}>
              The same quality, every single delivery.
            </h1>
            <p style={{ color: "rgba(255,255,255,.8)", fontSize: "clamp(15px,1.8vw,18px)", maxWidth: 480, marginBottom: "var(--s-8)", lineHeight: 1.6 }}>
              Grown in a controlled climate 365 days a year, so what lands on your pass never depends on the weather, the season, or a supplier's bad week. No pesticides. Harvested hours before it reaches you.
            </p>
            <div style={{ display: "flex", gap: "var(--s-4)", flexWrap: "wrap" }}>
              <Link to="/contact" className="btn btn-primary">Start a conversation</Link>
              <Link to="/browse-products?category=all" className="btn btn-ghost" style={{ color: "#fff", borderColor: "rgba(255,255,255,.4)" }}>See current products</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* KPI strip */}
      <div className="container">
        <motion.div
          className="partner-stats"
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
        >
          {kpis.map((k, i) => (
            <motion.div key={i} className="partner-stat" variants={fade}>
              <span className="partner-stat-icon" aria-hidden="true"><k.icon size={18} /></span>
              <strong className="partner-stat-value">{k.num}</strong>
              <span className="partner-stat-label">{k.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <div className="container section partner" style={{ padding: "0px", paddingTop: 0 }}>

        {/* BENEFITS - including EHO callout as a fourth card */}
        <p className="eyebrow" style={{ padding: "0 20px", marginTop: "var(--s-8)" }}>Why it works</p>
        <h2 className="h3" style={{ padding: "0 20px", marginBottom: 0 }}>Built for kitchens that can't compromise</h2>
        <motion.section
          className="partner-benefits"
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
        >
          {benefits.map((b, i) => (
            <motion.article key={i} className="benefit-card" variants={fade}>
              <div className="benefit-photo-wrap">
                {b.img ? (
                  <img className="benefit-photo" src={b.img} alt={b.alt} loading="lazy" />
                ) : (
                  <div className="benefit-photo benefit-photo--placeholder" aria-label={b.alt}>Photo coming soon</div>
                )}
                <span className="benefit-badge" aria-hidden="true"><b.icon size={18} /></span>
              </div>
              <div className="benefit-card__body">
                <h3 className="h3" style={{ margin: 0 }}>{b.title}</h3>
                <p className="muted" style={{ marginTop: 6 }}>{b.body}</p>
              </div>
            </motion.article>
          ))}

          {/* EHO callout - same .benefit-card sizing as siblings */}
          <motion.article className="benefit-card" variants={fade}>
            <div className="eho-callout">
              <EhoBadge size="lg" showLabel={false} />
              <div className="eho-callout__body">
                <h3 className="h3" style={{ margin: "0 0 var(--s-1)" }}>Rated 5/5 by Rochford EHO</h3>
                <p className="muted" style={{ margin: 0, fontSize: "var(--fs-14)" }}>
                  Independently inspected and awarded the maximum Food Hygiene Rating.
                </p>
              </div>
            </div>
          </motion.article>
        </motion.section>

        {/* SPECS */}
        <div className="specs-band">
          <p className="eyebrow" style={{ marginBottom: "var(--s-3)" }}>The details, up front</p>
          <section className="partner-specs">
            {specs.map((s, i) => (
              <motion.div key={i} className="spec" variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
                <span className="spec-icon" aria-hidden="true"><s.icon size={18} /></span>
                <h4 className="h4">{s.title}</h4>
                <p className="muted">{s.body}</p>
              </motion.div>
            ))}
          </section>
        </div>

        {/* FAQ */}
        <section className="partner-faq">
          <h2 className="h3">Common questions</h2>
          <ul className="faq-list" role="list">
            {faqs.map((f, i) => (
              <li key={i} className="faq-item">
                <details>
                  <summary>
                    <span>{f.q}</span>
                    <ChevronDown className="faq-chevron" size={18} aria-hidden="true" />
                  </summary>
                  <p className="muted">{f.a}</p>
                </details>
              </li>
            ))}
          </ul>
        </section>

        {/* ENQUIRY FORM */}
        <section className="partner-cta partner-enquiry">
          <div className="contact-form-section">
            <div className="partner-enquiry__head">
              <span className="spec-icon" aria-hidden="true"><MessageSquareText size={18} /></span>
              <h2 className="h3" style={{ margin: 0 }}>Let's plan your first order</h2>
            </div>
            <form ref={formRef} className="contact-form" onSubmit={handleEnquirySubmit} noValidate>
              <input
                type="text"
                name="b_username_verification"
                style={{ display: "none" }}
                tabIndex={-1}
                autoComplete="off"
              />

              <label htmlFor="businessName">Business name</label>
              <input id="businessName" name="businessName" required minLength={2} />

              <label htmlFor="partnerEmail">Email</label>
              <input id="partnerEmail" name="email" type="email" required />

              <label htmlFor="partnerMessage">What do you need?</label>
              <textarea
                id="partnerMessage"
                name="message"
                rows={5}
                required
                minLength={10}
                maxLength={1500}
                onInput={(e) => setMsgCount(e.currentTarget.value.length)}
              />
              <div className="muted small-count">{msgCount}/1500</div>

              <button type="submit" className="btn btn-primary" disabled={loading} aria-busy={loading}>
                {loading ? "Sending…" : "Send enquiry"}
              </button>

              <div aria-live="polite" className="form-live">
                {status === "success" && (
                  <p className="form-msg success">Thanks! I’ll get back to you soon.</p>
                )}
                {status === "error" && (
                  <p className="form-msg error">
                    {apiErr}{" "}
                    <a className="footer-link" href="mailto:contact@teardrophydro.co.uk">Open email</a>
                  </p>
                )}
              </div>
            </form>
          </div>
        </section>

      </div>
    </Layout>
  );
}
